import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  inject,
} from '@angular/core';
import { AuthorizationModule } from '@wilson/authorization';
import { FeatureFlagsModule } from '@wilson/feature-flags';
import {
  FeatureName,
  UserKpisInMinutes,
  UserTimelines,
} from '@wilson/interfaces';
import {
  SharedComponentsModule,
  UserDetailsModule,
} from '@wilson/shared-components';
import {
  OvertimeDataSourceService,
  ProductivityDataSourceService,
  UserKpisDataSourceService,
} from '@wilson/shift-timeline/assignments-gantt-chart';
import { OvertimeValueComponent } from '@wilson/work-time-management';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { Observable, map } from 'rxjs';
import { ProductivityKpiComponent } from '../user-timelines-KPIs/productivity-kpi/productivity-kpi.component';
import { KpiMode, KpiValueComponent } from './kpi-value/kpi-value.component';
import { TranslateModule } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { ShiftTimelineDataState } from '@wilson/shift-timeline/state';
import { HomeLocationValueComponent } from './home-location-value/home-location-value.component';

@Component({
  selector: 'wilson-user-timelines-info-card-kpis',
  templateUrl: './user-timelines-info-card-kpis.component.html',
  styleUrls: ['./user-timelines-info-card-kpis.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    SharedComponentsModule,
    NzTooltipModule,
    NzSkeletonModule,
    OvertimeValueComponent,
    UserDetailsModule,
    AuthorizationModule,
    FeatureFlagsModule,
    ProductivityKpiComponent,
    NzDividerModule,
    KpiValueComponent,
    TranslateModule,
    HomeLocationValueComponent,
    FeatureFlagsModule,
  ],
})
export class UserTimelinesInfoCardKpisComponent implements OnChanges {
  private overtimeDataSourceService = inject(OvertimeDataSourceService);
  private productivityDataSourceService = inject(ProductivityDataSourceService);
  private userKpisDataSourceService = inject(UserKpisDataSourceService);
  private store = inject(Store);

  @Input() user!: UserTimelines;
  @Input() renderOvertimeAccountKpi!: boolean;
  @Input() renderProductivityKpi!: boolean;
  @Input() renderWorkTimeWeekKpi!: boolean;
  @Input() renderWorkTimeMonthKpi!: boolean;
  @Input() renderPlannedTimeWeekKpi!: boolean;
  @Input() renderPlannedTimeMonthKpi!: boolean;
  @Input() renderOvertimeWeekKpi!: boolean;
  @Input() renderOvertimeMonthKpi!: boolean;
  @Input() renderHomeLocation!: boolean;

  readonly isLoadingUsersProductivity$ =
    this.productivityDataSourceService.isLoadingUsersProductivityStream();
  readonly loadingProductivityUserIds$ =
    this.productivityDataSourceService.loadingProductivityUserIdsStream();
  readonly isLoadingUsersOvertimes$ =
    this.overtimeDataSourceService.isLoadingUsersOvertimesStream();
  readonly fetchingWeeklyKpiForUserIds$ =
    this.userKpisDataSourceService.fetchingWeeklyKpiForUserIdsStream();
  readonly fetchingMonthlyKpiForUserIds$ =
    this.userKpisDataSourceService.fetchingMonthlyKpiForUserIdsStream();
  readonly updatingKpiUserIds$ =
    this.userKpisDataSourceService.updatingKpiUserIdsStream();

  visibleDateRange$ = this.productivityDataSourceService.visibleDateRange();
  userOvertime$!: Observable<number | undefined>;
  userProductivity$!: Observable<number | undefined>;
  userKpisWeek$!: Observable<UserKpisInMinutes | undefined>;
  userKpisMonth$!: Observable<UserKpisInMinutes | undefined>;
  FeatureName = FeatureName;
  KpiMode = KpiMode;
  currentDate$ = this.store.select(ShiftTimelineDataState.currentDate);

  ngOnChanges(simpleChanges: SimpleChanges): void {
    if (simpleChanges['user']) {
      this.initOvertimes();
    }
  }

  initOvertimes(): void {
    const userId = this.user.id;

    if (userId) {
      this.userOvertime$ =
        this.overtimeDataSourceService.userOvertimeMinutesStream(userId);

      this.userKpisWeek$ =
        this.userKpisDataSourceService.userKpisWeekStream(userId);
      this.userKpisMonth$ =
        this.userKpisDataSourceService.userKpisMonthStream(userId);

      this.userProductivity$ = this.productivityDataSourceService
        .userProductivityStream(userId)
        .pipe(
          map((productivity) => {
            if (productivity) {
              return parseFloat((productivity * 100).toFixed(2));
            } else {
              return 0;
            }
          }),
        );
    }
  }
}
